import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type { FC } from "react";
import { Button, Modal, Progress } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ContainerAggregation } from "../containers/components/ContainerAddForm";
import { ToFirstWarehouseRegistry } from "../../security";
import { ContainerResource, getContainers, getWareHouses } from "api/generated";
import { handleAxiosError } from "common/helpers/generalUtils";
import { useClient } from "hooks/useClient";
import DistributionPointSelected from "context/MyDistributionPoint";
import FirstListContainersSaved from "./components/FirstListContainerSaved";

const FirstContainerRegistry: FC = () => {
  const [warehouse, setWarehouse] = useState(false);
  const [containersSaved, setContainersSaved] = useState<ContainerResource[]>(
    [],
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  const hasFetchedWarehouses = useRef(false);
  const hasShownModal = useRef(false);

  const { distributionPoint } = useContext(DistributionPointSelected);

  const { client } = useClient();

  const fetchContainers = useCallback(() => {
    getContainers({
      client,
      path: { distributionPointId: distributionPoint?.id ?? "" },
    })
      .then(({ data }) => setContainersSaved(data ?? []))
      .catch((err) =>
        handleAxiosError(err, "No se pudieron obtener los almacenes"),
      );
  }, [client, distributionPoint]);

  useEffect(() => {
    if (containersSaved.length === 0) {
      setDrawerOpen(true);
    }
  }, [containersSaved]);

  const showInfoModal = useCallback(() => {
    Modal.info({
      title: "Atención",
      content: (
        <div>
          <br />
          <br />
          <p style={{ fontSize: "20px" }}>
            La información agregada al registrar contenedores no puede ser
            editada, por lo cual es de suma importancia ingresar y verificar la
            información cuidadosamente ya que este registro queda a
            responsabilidad del usuario.
          </p>
          <br />
        </div>
      ),
      width: 1000,
    });
  }, []);

  const fetchWarehouses = useCallback(() => {
    getWareHouses({ client })
      .then(({ data }) => {
        const hasWarehouse = (data?.length ?? 0) > 0;
        setWarehouse(hasWarehouse);
        if (hasWarehouse) {
          fetchContainers();
        }
        if (!hasShownModal.current) {
          hasShownModal.current = true;
          showInfoModal();
        }
      })
      .catch((err) =>
        handleAxiosError(err, "No se pudieron obtener los almacenes"),
      );
  }, [fetchContainers, client, showInfoModal]);

  useEffect(() => {
    if (!hasFetchedWarehouses.current) {
      hasFetchedWarehouses.current = true;
      fetchWarehouses();
    }
  }, [fetchWarehouses]);

  const handleSuccess = useCallback(() => {
    setDrawerOpen(false);
    fetchContainers();
  }, [fetchContainers]);

  return (
    <div>
      {warehouse ? (
        <div>
          {containersSaved.length > 0 ? (
            <FirstListContainersSaved containersSaved={containersSaved} />
          ) : (
            <div>
              <span style={{ fontSize: "24px" }}>
                Sin contenedores agregados
              </span>
            </div>
          )}
          <div style={{ marginTop: 24 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setDrawerOpen(true)}
            >
              Agregar contenedor
            </Button>
          </div>

          <ContainerAggregation
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onSuccess={handleSuccess}
            isInitialConfiguration
          />
        </div>
      ) : (
        <div className="center-div">
          <Progress type="circle" percent={70} status="exception" />
          <div style={{ margin: "8px" }}>
            <span>
              Por favor registra algún almacén antes de añadir contenedores
            </span>
          </div>
          <Button
            className="back-button"
            key="back"
            onClick={ToFirstWarehouseRegistry}
          >
            Registrar almacén
          </Button>
        </div>
      )}
    </div>
  );
};

export default FirstContainerRegistry;
